import React, { useEffect, useState, useMemo } from "react";

import { useSelector } from "react-redux";
import {
  RideSelectTextField,
  RideSelect,
  RideTextField,
  RideRadioButton,
  RideRadioButtonGroup,
} from "@rimac-seguros/ride-system-components";

import { CustomDropdown } from "@/components/molecules/CustomDropdown/index";
import { LIST_TYPE_ROAD } from "@/constants/list-type-roads";
import { useUbigeo } from "@/hooks/useUbigeo";
import { CustomListBottomSheet } from "@/components/molecules/CustomListBottomSheet";

import "./styles.scss";

const FormUbigeo = ({
  register,
  errors,
  setError,
  setValue,
  watch,
  isSmallScreen,
  loading,
  setLoading,
}) => {
  const {
    departamentos,
    departamentoLoading,
    provinciaLoading,
    provincias,
    distritos,
    distritoLoading,
    selectedDepartamento,
    selectedDistrito,
    selectedProvincia,
    nombreVia,
    tipoVia,
    numeroVia,
    validUbigeo,
  } = useSelector((state) => state.domicilio);
  const { userData } = useSelector((state) => state.vida);

  const [openStreetType, setOpenStreetType] = useState(false);

  const {
    onChangeDepartment,
    onChangeProvince,
    onChangeDistrict,
    getProvinceDistrictTogether,
    setCurrentDepartment,
  } = useUbigeo();

  const isValidRoadType = (roadType) => {
    return LIST_TYPE_ROAD.some((item) => item.value === roadType);
  };

  const configDefaultData = async () => {
    if (selectedDepartamento && selectedDepartamento.value) {
      setLoading(true);
      setCurrentDepartment(selectedDepartamento.value);
      await getProvinceDistrictTogether(
        selectedDepartamento.value,
        selectedProvincia.value
      );
      setValue("department", selectedDepartamento.value);
      setValue("province", selectedProvincia.value);
      setValue("district", selectedDistrito.value);

      const cleanStreetName = (nombreVia || "").replace(/[^a-zA-Z0-9 ]/g, "");
      setValue("address", cleanStreetName);

      const cleanStreetNumber = (numeroVia || "")
        .toString()
        .replace(/[^0-9]/g, "");
      if (cleanStreetNumber) {
        setValue("streetNumber", cleanStreetNumber);
        if (cleanStreetName.length > 0 && cleanStreetName.length < 3) {
          setError("address", {
            message: "Debes ingresar mínimo 3 caracteres",
            type: "minLength",
          });
        }
      }

      const streetType = isValidRoadType(tipoVia?.value) ? tipoVia.value : "AV";
      setValue("streetType", streetType, { shouldValidate: true });

      setLoading(false);
    }
  };

  useEffect(() => {
    configDefaultData();
  }, []);

  const optionDepartments = useMemo(
    () =>
      departamentos.map((item) => ({
        label: item.descripcionDepartamento,
        value: item.ideDepartamento,
      })),
    [departamentos]
  );

  const optionProvinces = useMemo(
    () =>
      provincias.map((item) => ({
        label: item.descripcionProvincia,
        value: item.ideProvincia,
      })),
    [provincias]
  );

  const optionDistricts = useMemo(
    () =>
      distritos.map((item) => ({
        label: item.descripcionDistrito,
        value: item.ideDistrito,
      })),
    [distritos]
  );

  const handleInputStreetNumber = (e) => {
    const el = e.target;
    el.value = el.value.replace(/[^0-9]/g, "");
  };

  return (
    <>
      <div
        id="form-ubigeo"
        className={`form-ubigeo ${validUbigeo ? "hidden" : "flex flex-col"}`}
      >
        <CustomDropdown
          name="department"
          label="Departamento"
          titleModal="Departamento"
          isShowModal={isSmallScreen}
          hasSearch
          iconResult="RideGlPinSolid"
          list={optionDepartments || []}
          loading={departamentoLoading || loading}
          disabled={departamentoLoading}
          register={register}
          setValue={setValue}
          watch={watch}
          placeholder="¿Dónde vives?"
          onSelect={(value) => {
            onChangeDepartment(value);
            setValue("province", "");
            setValue("district", "");
          }}
          error={!!errors?.department}
          errorMessage="Campo requerido"
          isEmbedded={userData.indPrevPageApp}
        />
        <div className="flex flex-col gap-ride-gl-spacing-2xlarge lg:flex-row">
          <CustomDropdown
            name="province"
            label="Provincia"
            titleModal="Provincia"
            isShowModal={isSmallScreen}
            hasSearch
            iconResult="RideGlPinSolid"
            list={optionProvinces || []}
            loading={provinciaLoading || loading}
            disabled={!watch("department") || provinciaLoading}
            register={register}
            setValue={setValue}
            watch={watch}
            placeholder="¿Dónde vives?"
            onSelect={(value) => {
              onChangeProvince(value);
              setValue("district", "");
            }}
            error={!!errors?.province}
            errorMessage="Campo requerido"
            isEmbedded={userData.indPrevPageApp}
          />
          <CustomDropdown
            name="district"
            label="Distrito"
            titleModal="Distrito"
            isShowModal={isSmallScreen}
            hasSearch
            iconResult="RideGlPinSolid"
            list={optionDistricts || []}
            loading={distritoLoading || loading}
            disabled={!watch("province") || distritoLoading}
            register={register}
            setValue={setValue}
            watch={watch}
            placeholder="¿Dónde vives?"
            onSelect={(value) => {
              onChangeDistrict(value);
            }}
            error={!!errors?.district}
            errorMessage="Campo requerido"
            isEmbedded={userData.indPrevPageApp}
          />
        </div>
        <div className="relative flex flex-col gap-ride-gl-spacing-2xlarge lg:flex-row">
          <RideSelectTextField
            label="Dirección"
            size="large"
            error={!!errors?.address}
            errorMessage={errors?.address?.message}
            className="w-full field-capitalize"
            loading={loading}
          >
            <RideSelect
              {...register("streetType", { required: true })}
              options={LIST_TYPE_ROAD}
              onOpenChange={(open) => isSmallScreen && setOpenStreetType(open)}
              value={watch("streetType")}
            />
            <RideTextField
              {...register("address", {
                required: "Este campo es obligatorio",
                minLength: {
                  value: 3,
                  message: "Debes ingresar mínimo 3 caracteres",
                },
                validate: (value) =>
                  value.trim().length >= 3 ||
                  "Debes ingresar mínimo 3 caracteres válidos",
              })}
              onChange={(e) => setValue("address", e.target.value)}
            />
          </RideSelectTextField>
          <div
            className="form-ubigeo_mask"
            onClick={() => setOpenStreetType(true)}
          />
          <CustomListBottomSheet
            title="Tipo de vía"
            list={LIST_TYPE_ROAD}
            isOpen={openStreetType}
            setIsOpen={setOpenStreetType}
            name="streetType"
            setValue={setValue}
            value={watch("streetType")}
          />
          <RideTextField
            label="Nro. de via"
            aria-required
            size="large"
            {...register("streetNumber", {
              required: "Este campo es obligatorio",
              maxLength: {
                value: 10,
                message: "Número de vía es incorrecto",
              },
            })}
            className="form-ubigeo_streetNumber xl:w-full"
            onInput={handleInputStreetNumber}
            onChange={(e) => setValue("streetNumber", e.target.value)}
            error={!!errors?.streetNumber}
            errorMessage={errors?.streetNumber?.message}
            loading={loading}
          />
        </div>
      </div>
      <RideRadioButtonGroup
        label="¿Tiene obligaciones tributarias fuera de Perú?"
        orientation="horizontal"
        error={!!errors?.hasTaxObligations}
        errorMessage="Campo requerido"
        className="mb-ride-gl-spacing-4xlarge"
      >
        <RideRadioButton
          text="Sí"
          value="Si"
          {...register("hasTaxObligations", { required: true })}
        />
        <RideRadioButton
          text="No"
          value="No"
          {...register("hasTaxObligations", { required: true })}
        />
      </RideRadioButtonGroup>
    </>
  );
};

export default FormUbigeo;
